export type CourseBadge = { index: number; total: number };

export function courseBadgeForStep(
  sortOrder: number | null | undefined,
  total: number | null | undefined,
): CourseBadge | null {
  if (!sortOrder || sortOrder < 1 || !total || total < 1) return null;
  return { index: sortOrder, total };
}

export type CourseForloebStep = {
  id: string;
  sort_order: number;
  status?: string;
};

function currentForloebStep(steps: readonly CourseForloebStep[]): CourseForloebStep | null {
  const ordered = [...steps].sort((a, b) => a.sort_order - b.sort_order);
  const nextOpen = ordered.find(
    (step) => step.status === 'upcoming' || step.status === 'unscheduled' || !step.status,
  );
  if (nextOpen) return nextOpen;
  return [...ordered].reverse().find((step) => step.status === 'completed') ?? ordered[0] ?? null;
}

/**
 * Shared Trin N af M: n is the current step's 1-based sort_order
 * (or its 1-based position if sort_order is missing). Progress copy uses
 * `completed`. Calendar badge, list, overview and portal must stay in sync.
 */
export function courseForloebProgress(steps: readonly CourseForloebStep[]): {
  n: number;
  total: number;
  completed: number;
} {
  const ordered = [...steps].sort((a, b) => a.sort_order - b.sort_order);
  const total = ordered.length;
  const completed = ordered.filter((step) => step.status === 'completed').length;
  const current = currentForloebStep(ordered);
  if (!current || total < 1) return { n: 0, total, completed };
  const position = ordered.indexOf(current) + 1;
  const sortOrder = current.sort_order >= 1 ? current.sort_order : position;
  const badge = courseBadgeForStep(sortOrder, total);
  return { n: badge?.index ?? position, total, completed };
}

export function applyCourseBadges<
  T extends {
    treatment_course_step_id?: string | null;
    course_badge?: CourseBadge | null;
    treatment_course_id?: string | null;
  },
>(
  rows: T[],
  steps: Array<{ id: string; sort_order: number; course_id: string }>,
  totalsByCourse: Map<string, number>,
): T[] {
  const byStep = new Map(steps.map((step) => [step.id, step]));
  return rows.map((row) => {
    const step = row.treatment_course_step_id
      ? byStep.get(row.treatment_course_step_id)
      : undefined;
    if (!step) return { ...row, course_badge: null, treatment_course_id: null };
    return {
      ...row,
      course_badge: courseBadgeForStep(step.sort_order, totalsByCourse.get(step.course_id) ?? 0),
      treatment_course_id: step.course_id,
    };
  });
}
